import { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Loading } from '../../ui/States'
import type { NtCourse, NtTerm, NtDay } from '../../core/types'
import { DAY_LABELS, PREP_DAY, STATUS_LABEL, partsFor } from './model'
import { useNorseTerm } from './NorseTermData'
import { CourseExemplarPanel } from './CourseExemplars'

/**
 * /norseterm/info, its own page: the page a teacher should be able to read cold, in
 * October, having never heard of Norse Term: what it is, the dates, the shape
 * of the week, how proposing a course works, and who else is teaching what.
 *
 * "Who's teaching what" leaves drafts out by name. A draft is the teacher's
 * own until they send it, so only the count shows.
 */
export function NorseTermInfo() {
  const { term, courses, roles, loading, markInfoRead } = useNorseTerm()
  // Opening it is reading it, as far as the "Read about Norse Term" step goes.
  useEffect(() => { markInfoRead() }, [markInfoRead])
  if (loading) return <Loading label="Loading Norse Term" />
  return (
    <>
      <Link to="/norseterm" className="backlink">← My Norse Term steps</Link>
      <div className="pagehead">
        <div>
          <h1 className="page-title" style={{ margin: 0 }}>About Norse Term</h1>
          <p style={{ color: 'var(--ink-2)', fontSize: 'var(--t-sm)', margin: '2px 0 0' }}>{term.weekLabel}</p>
        </div>
      </div>
      <section className="card" style={{ marginTop: 'var(--s-2)' }}>
        <h2 className="ov__h">What Norse Term is</h2>
        <p className="ov__p">
          Four days in March when the high school sets the regular schedule aside. Teachers offer short,
          hands-on courses; every student takes one in the morning and one in the afternoon, or does an
          independent project instead. Each student makes one artifact for the open house on Friday.
        </p>
      </section>

      {/* The point of the week, said before any logistics: the best courses
          come from what a teacher genuinely loves, not from the curriculum. */}
      <section className="card ov__love" style={{ marginTop: 'var(--s-3)' }}>
        <h2 className="ov__h">Teach what you love</h2>
        <p className="ov__p">
          Norse Term is your chance to share the thing you&apos;re into outside your subject: the hobby you
          lose weekends to, the craft you picked up from a grandparent, the question you keep reading about,
          the skill you wish someone had taught you at fifteen. It doesn&apos;t have to match what you teach
          the rest of the year, and you don&apos;t have to be an expert. You just have to care about it.
        </p>
        <p className="ov__p" style={{ margin: 0 }}>
          Students can tell when a teacher is genuinely excited, and that is what they remember about the
          week. Start with what you&apos;d happily talk about for an hour if a student asked, then build the
          three days around getting them hooked on it too.
        </p>
      </section>

      {roles.teach && <TeacherSteps term={term} />}
      <KeyDates term={term} />
      <TheWeek term={term} />
      <section style={{ marginTop: 'var(--s-3)' }} id="examples">
        <CourseExemplarPanel defaultOpen />
      </section>
      <WhoIsTeaching courses={courses ?? []} />
    </>
  )
}

/* ---------------- dates ---------------- */

/** ISO dates come out of a date input; nobody reads 2027-01-16. */
export function fmtDate(v: string, opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' }): string {
  const d = new Date(v + 'T12:00:00')
  return isNaN(d.getTime()) ? v : d.toLocaleDateString(undefined, opts)
}

/** The calendar date of a Norse Term day (0 = Tuesday), or null with no start date. */
function dateOf(term: NtTerm, day: number): string | null {
  if (!term.startDate) return null
  const d = new Date(term.startDate + 'T12:00:00')
  if (isNaN(d.getTime())) return null
  d.setDate(d.getDate() + day)
  return d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })
}

function KeyDates({ term }: { term: NtTerm }) {
  const d = term.deadlines
  const week = dateOf(term, 0) && dateOf(term, PREP_DAY)
  const rows: Array<[string, string | null, string]> = [
    ['Course proposals due', d.courses ? fmtDate(d.courses) : null, 'Teachers send their course for review'],
    ['Independent project proposals due', d.proposals ? fmtDate(d.proposals) : null, 'Students doing a project instead of courses submit their proposal'],
    ['Student course selections due', d.rankings ? fmtDate(d.rankings) : null, 'Students choose and rank their top courses from the approved catalog'],
    ['Norse Term', week ? `${dateOf(term, 0)} – ${dateOf(term, PREP_DAY)}` : term.weekLabel, 'Tuesday to Friday'],
    ['Open house', dateOf(term, PREP_DAY), 'Friday, after morning prep'],
  ]
  return (
    <section className="card" style={{ marginTop: 'var(--s-3)' }}>
      <h2 className="ov__h">Key dates</h2>
      <ul className="ov__dates">
        {rows.map(([what, when, note]) => (
          <li key={what}>
            <div className="ov__when">{when ?? 'Not set yet'}</div>
            <div>
              <div style={{ fontWeight: 610 }}>{what}</div>
              <div className="work__sub">{note}</div>
            </div>
          </li>
        ))}
      </ul>
    </section>
  )
}

/* ---------------- the week ---------------- */

function TheWeek({ term }: { term: NtTerm }) {
  const span = (day: NtDay, block: 'AM' | 'PM') => {
    const p = partsFor(term, day, block)
    return p.length ? `${p[0]!.start}–${p[p.length - 1]!.end}` : ''
  }
  const early = term.dayStructure?.earlyOutDays ?? [1]
  return (
    <section className="card" style={{ marginTop: 'var(--s-3)' }}>
      <h2 className="ov__h">The week</h2>
      <p className="ov__p">
        Each course meets in one block, morning or afternoon, for three days, in two parts with a break
        between. Friday morning, students work on their artifact in whichever course they chose to make it in.
        <strong> Friday is on campus for everyone</strong>, independent project students included.
      </p>
      <ul className="ov__week">
        {(DAY_LABELS as readonly string[]).map((label, i) => {
          const day = i as NtDay
          return (
            <li key={label}>
              <div className="ov__day">
                {label}
                {dateOf(term, i) && <span className="work__sub"> · {dateOf(term, i)}</span>}
              </div>
              {day === PREP_DAY ? (
                <div className="ov__slots">
                  <span><strong>Morning</strong> {span(day, 'AM')} · artifact & open-house prep</span>
                  <span><strong>Then</strong> the open house</span>
                </div>
              ) : (
                <div className="ov__slots">
                  <span><strong>Morning course</strong> {span(day, 'AM')}</span>
                  <span><strong>Afternoon course</strong> {span(day, 'PM')}</span>
                  {early.includes(day) && <span className="pill">Early out</span>}
                </div>
              )}
            </li>
          )
        })}
      </ul>
    </section>
  )
}

/* ---------------- how proposing works ---------------- */

/**
 * The five steps of proposing a course, each linking to where it happens.
 */
function TeacherSteps({ term }: { term: NtTerm }) {
  const due = term.deadlines.courses ? fmtDate(term.deadlines.courses, { month: 'long', day: 'numeric' }) : null
  const rank = term.deadlines.rankings ? fmtDate(term.deadlines.rankings, { month: 'long', day: 'numeric' }) : null
  const steps: Array<[string, React.ReactNode, string?]> = [
    ['Pick something you love, then read a few example courses',
      <>Start with what you&apos;re passionate about. Then look at the sample courses, each with its
        description, what students make for the open house, and the full three-day outline, to see how an
        interest becomes a course.</>,
      '/norseterm/examples'],
    ['Create your course overview',
      <>Write it the way you&apos;d pitch it to a student in the hallway: what you love about it and what
        they&apos;ll get to do. Your draft <strong>saves automatically</strong> as you type. Close it, come back next week, pick up
        where you left off. Nobody reviews it until you send it.</>,
      '/norseterm/mine'],
    ['Fill in the overview, then plan the three days',
      <>First the <strong>course overview</strong>: a title and description students will read, room, seats and
        costs, and what students can make for the open house. Then <strong>plan the three days</strong>, part by
        part. It doesn&apos;t have to be finished in one sitting.</>],
    ['Send it for review',
      <>{due ? <>By <strong>{due}</strong>. </> : null}An administrator approves it, or sends it back with a note
        saying what to change. You edit it and send it again.</>],
    ['Approved courses go in the catalog',
      <>Students make their course selections from it{rank ? <> by {rank}</> : null}, and your roster appears here once placement runs.</>],
  ]
  return (
    <section className="card" style={{ marginTop: 'var(--s-3)' }}>
      <h2 className="ov__h">What we need from you</h2>
      <ol className="ov__steps">
        {steps.map(([title, body, to], i) => (
          <li key={i}>
            <span className="ov__num" aria-hidden="true">{i + 1}</span>
            <div>
              <div style={{ fontWeight: 620 }}>
                {to ? <Link to={to}>{title} →</Link> : title}
              </div>
              <div className="ov__p" style={{ margin: '2px 0 0' }}>{body}</div>
            </div>
          </li>
        ))}
      </ol>
    </section>
  )
}

/* ---------------- who is teaching what ---------------- */

const ORDER: Record<NtCourse['status'], number> = { approved: 0, submitted: 1, returned: 2, draft: 3 }

function WhoIsTeaching({ courses }: { courses: NtCourse[] }) {
  const shown = courses.filter((c) => c.status !== 'draft')
    .sort((a, b) => ORDER[a.status] - ORDER[b.status] || a.teacherName.localeCompare(b.teacherName))
  const drafts = courses.length - shown.length
  const block = (c: NtCourse) => c.block === 'AM' ? 'Morning' : c.block === 'PM' ? 'Afternoon'
    : c.constraint === 'AM' ? 'Morning (preferred)' : c.constraint === 'PM' ? 'Afternoon (preferred)' : 'Either'
  const pill = (c: NtCourse) => (
    <span className={`pill ${c.status === 'approved' ? 'pill--good' : ''}`}>
      {c.status === 'approved' ? '✓ ' : ''}{c.status === 'returned' ? 'In review' : STATUS_LABEL[c.status]}
    </span>
  )
  return (
    <section className="card" style={{ marginTop: 'var(--s-3)' }}>
      <h2 className="ov__h">Who&apos;s teaching what</h2>
      <p className="ov__p">
        {shown.length === 0
          ? 'No courses have been sent for review yet.'
          : `${shown.length} course${shown.length === 1 ? '' : 's'} sent for review so far.`}
        {drafts > 0 && ` ${drafts} more ${drafts === 1 ? 'is' : 'are'} being drafted.`}
      </p>
      {shown.length > 0 && (
        <>
          <table className="table">
            <thead><tr><th>Course</th><th>Teacher</th><th>Block</th><th>Status</th></tr></thead>
            <tbody>
              {shown.map((c) => (
                <tr key={c.id}>
                  <td style={{ fontWeight: 610 }}>{c.title}</td>
                  <td>{c.teacherName}</td>
                  <td>{block(c)}</td>
                  <td>{pill(c)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <ul className="work cards">
            {shown.map((c) => (
              <li key={c.id} className="work__row">
                <div className="work__main">
                  <div style={{ fontWeight: 610 }}>{c.title}</div>
                  <div className="work__sub">{c.teacherName} · {block(c)}</div>
                </div>
                {pill(c)}
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  )
}
